import { apiClient } from "./api";
import { queryOptions } from "@tanstack/react-query";

// Types
export interface Thread {
  doc_id: string;
  persona_id: string;
  tweets: string[];
  metadata: {
    topic?: string;
    video_url?: string;
    created_at: string;
  };
  preview?: string;
  deleted?: boolean;
  deleted_at?: string;
}

export interface TopicThreadCreate {
  topic: string;
  thread_length?: number;
}

export interface VideoThreadCreate {
  youtube_url: string;
  thread_length?: number;
  user_guidance?: string;
}

export interface ThreadsResponse {
  data: Thread[];
  pagination: {
    cursor: string | null;
  };
}

// Create a thread from a topic
export const createTopicThread = async (
  persona_id: string,
  data: TopicThreadCreate,
): Promise<{ thread_id: string }> => {
  try {
    //console.log("Creating topic thread with:", { persona_id, ...data });
    const response = await apiClient.post("/api/threads/create/topic", {
      ...data,
      persona_id,
    });
    console.log("Topic thread creation response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error creating topic thread:", error);
    throw error;
  }
};

// Create a thread from a YouTube video
export const createVideoThread = async (
  persona_id: string,
  data: VideoThreadCreate,
): Promise<{ thread_id: string }> => {
  try {
    //console.log("Creating video thread with:", { persona_id, ...data });
    const response = await apiClient.post("/api/threads/create/video", {
      ...data,
      persona_id,
    });
    console.log("Video thread creation response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error creating video thread:", error);
    throw error;
  }
};

// Post a stored thread
export const postThread = async (
  persona_id: string,
  thread_id: string,
): Promise<{ tweet_ids: string[] }> => {
  try {
    //console.log("Posting thread:", { persona_id, thread_id });
    const response = await apiClient.post(`/api/threads/post/${thread_id}`, {
      persona_id,
    });
    //console.log("Thread posting response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error posting thread:", error);
    throw error;
  }
};

// Get list of threads for a persona
export const getThreads = async (
  persona_id: string,
  cursor?: string,
  limit: number = 10,
): Promise<ThreadsResponse> => {
  try {
    console.log("Fetching threads for:", { persona_id, cursor, limit });
    const response = await apiClient.post("/api/threads/list", {
      persona_id,
      cursor,
      limit,
    });
    //console.log("Threads fetch response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching threads:", error);
    throw error;
  }
};

// Get a specific thread
export const getThread = async (
  thread_id: string,
  persona_id: string,
): Promise<Thread> => {
  try {
    console.log("Fetching specific thread:", { thread_id, persona_id });
    const response = await apiClient.post(`/api/threads/${thread_id}`, {
      persona_id,
    });
    //console.log("Thread fetch response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching thread:", error);
    throw error;
  }
};

// React Query Options
export const threadQueries = {
  threads: (persona_id: string, cursor?: string, limit: number = 10) =>
    queryOptions({
      queryKey: ["threads", persona_id, cursor, limit],
      queryFn: () => getThreads(persona_id, cursor, limit),
      staleTime: 0, // Always fetch fresh data
      gcTime: 5 * 60 * 1000, // Cache for 5 minutes
      retry: 3, // Retry failed requests 3 times
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    }),
  thread: (thread_id: string, persona_id: string) =>
    queryOptions({
      queryKey: ["thread", thread_id, persona_id],
      queryFn: () => getThread(thread_id, persona_id),
      staleTime: 0,
      retry: 3,
    }),
};
